import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ReactComponent as ClockIcon } from "./arrow.svg";
import PropTypes from "prop-types";
import ClassNames from "classnames";
import ROUTES from "Constants/routes";
import PROFILE_FIELD from "Constants/profileFields";

import Badge from "@propellerads/badge";
import {
  Sent,
  Site,
  Stat,
  Code,
  People,
  Target,
  Postback,
  User,
  Money,
  Date,
  InfoOutline,
} from "@propellerads/icon";

import Translate from "Components/Translate";

import "./styles.scss";
import { useAppSelector } from "../../store/configureStore";

let MENU_ITEMS = [
  {
    state: ROUTES.SENDINGS,
    key: "left_menu_notifications",
    text: <Translate tag="left_menu_notifications" />,
    icon: <Sent />,
    subLevel: [
      {
        state: ROUTES.RSS_LIST,
        key: "left_menu_rss",
        text: <Translate tag="left_menu_rss" />,
      },
    ],
  },
  {
    state: ROUTES.SITES.LIST,
    key: "left_menu_sources",
    text: <Translate tag="left_menu_sources" />,
    icon: <Site />,
  },
  {
    state: ROUTES.STATISTICS.BASE,
    key: "left_menu_statistics",
    text: <Translate tag="left_menu_statistics" />,
    icon: <Stat />,
  },
  {
    state: ROUTES.STATISTICS.ADV,
    key: "left_menu_statistics_adv",
    text: <Translate tag="left_menu_statistics" />,
    icon: <Money />,
    isNew: true,
  },
  {
    state: ROUTES.SUBSCRIPTIONS,
    key: "left_menu_subscriptions",
    text: <Translate tag="left_menu_subscriptions" />,
    icon: <People />,
  },
  {
    state: ROUTES.AUDIENCES,
    key: "left_menu_audiences",
    text: <Translate tag="left_menu_audiences" />,
    icon: <Target />,
  },
  {
    state: ROUTES.PIXELS,
    key: "left_menu_audience-pixels",
    text: <Translate tag="left_menu_audience-pixels" />,
    icon: <User />,
  },
  {
    state: ROUTES.AUTH.API_ACCESS,
    key: "profile_api_access",
    text: <Translate tag="profile_api_access" />,
    icon: <Code />,
  },
  {
    state: ROUTES.INTEGRATIONS,
    key: "left_menu_integrations",
    text: <Translate tag="left_menu_integrations" />,
    icon: <Postback />,
  },
  {
    state: ROUTES.BILLING,
    key: "left_menu_billing",
    text: <Translate tag="left_menu_billing" />,
    icon: <Date />,
    isNew: true,
  },
  {
    state: ROUTES.WELCOME,
    key: "left_menu_insights",
    text: <Translate tag="left_menu_insights" />,
    icon: <InfoOutline />,
  },
];

// eslint-disable-next-line no-unused-vars
const Sidebar = ({ isOpen, onClose, profile, isEnabledBilling }) => {
  const {
    loading: loadingProfile,
  } = useAppSelector((store) => store.notixProfile);

  const location = useLocation();

  const advStatsAvailable =
    profile?.[PROFILE_FIELD.LINKED_PUBLISHER_ID] &&
    profile[PROFILE_FIELD.LINKED_PUBLISHER_ID] > 0;

  const style = ClassNames("Sidebar", {
    Sidebar_isOpen: isOpen,
    Sidebar_loading: loadingProfile,
  });

  const [showedSubMenu, setShowedSubMenu] = useState({});

  useEffect(() => {
    MENU_ITEMS.forEach((item) => {
      if (item.key === "left_menu_statistics_adv") {
        if (advStatsAvailable) {
          item.state = ROUTES.STATISTICS.ADV;
        }
      }
    });
  }, [advStatsAvailable]);

  useEffect(() => {
    setShowedSubMenu({
      [location.pathname]: true,
    });

    let path = "";
    MENU_ITEMS.forEach((item) => {
      item.subLevel?.forEach((subLevelItem) => {
        if (location.pathname === subLevelItem.state) {
          path = item.state;
        }
      });
    });

    if (path) {
      setShowedSubMenu({
        [path]: true,
      });
    }
  }, [location.pathname]);

  const showSubMenuHandle = (state) => {
    if (showedSubMenu[state]) {
      const actualState = { ...showedSubMenu };
      delete actualState[state];
      setShowedSubMenu(actualState);
    } else {
      setShowedSubMenu({ ...showedSubMenu, [state]: true });
    }
  };

  const renderSubMenu = (data) => {
    if (showedSubMenu[data.state]) {
      if (isOpen) {
        return data.subLevel.map((item, key) => {
          return (
            <NavLink
              key={`${item.state}${key}`}
              to={item.state}
              className={ClassNames("Sidebar__sub-item", {
                "Sidebar__sub-item_closed": !isOpen,
              })}
              activeClassName="Sidebar__sub-item_active"
            >
              {renderMenuItem(item, true)}
            </NavLink>
          );
        });
      } else {
        return (
          <div className="link-with-child__wrapper">
            {data.subLevel.map((item, key) => {
              return (
                <NavLink
                  to={item.state}
                  key={`${item.state}${key}`}
                  className={ClassNames("Sidebar__sub-item", {
                    "Sidebar__sub-item_closed": !isOpen,
                  })}
                  activeClassName="Sidebar__sub-item_active"
                >
                  {renderMenuItem(item, true)}
                </NavLink>
              );
            })}
          </div>
        );
      }
    }
  };

  const renderNav = (item, key) => {
    if (loadingProfile) {
      return null;
    }

    if (item.state === ROUTES.BILLING && !isEnabledBilling) {
      return null;
    }

    if (
      item.key === "left_menu_statistics_adv" && !advStatsAvailable
    ) {
      return null;
    }

    return (
      <div key={`${item.key}${key}`}>
        {item.subLevel ? (
          <div
            className={ClassNames("link-with-child", {
              "link-with-child_closed": !isOpen,
            })}
          >
            <div className="link-with-child__row">
              <NavLink
                to={item.state}
                key={item.state}
                className={ClassNames("Sidebar__item", {
                  Sidebar__item_active: showedSubMenu[item.state],
                })}
                activeClassName="Sidebar__item_active"
              >
                {renderMenuItem(item)}
              </NavLink>
              <div
                className={ClassNames("link-with-child__arrow", {
                  "link-with-child__arrow_active":
                    showedSubMenu[item.state] ||
                    item.state === location.pathname,
                })}
                onClick={() => showSubMenuHandle(item.state)}
              >
                <ClockIcon />
              </div>
            </div>
            {renderSubMenu(item)}
          </div>
        ) : (
          <NavLink
            to={item.state}
            key={item.state}
            className="Sidebar__item"
            activeClassName="Sidebar__item_active"
          >
            {renderMenuItem(item)}
          </NavLink>
        )}
      </div>
    );
  };

  const renderMenuItem = (item, isSub) => (
    <>
      {item.icon}
      {isOpen && !isSub && (
        <span className="Sidebar__itemLabel">
          {<Translate tag={item.key} />}
        </span>
      )}
      {item.isNew && isOpen && (
        <Badge
          label={<Translate tag="new" />}
          customStyle={{ color: "#FF6036", background: "#FFFFFF00" }}
        />
      )}
      {isSub && (
        <span className="Sidebar__itemLabel">
          {<Translate tag={item.key} />}
        </span>
      )}
    </>
  );

  return (
    <div className={style}>
      <div className="Sidebar__menu" key="Sidebar__menu">
        <div>{MENU_ITEMS.map((item, key) => renderNav(item, key))}</div>
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  profile: PropTypes.object.isRequired,
  isEnabledBilling: PropTypes.bool,
};

Sidebar.defaultProps = {
  isOpen: true,
  onClose: undefined,
};

export default Sidebar;
